import { motion, MotionValue, useInView, useScroll, useTransform, Variants } from "framer-motion";
import { ReactNode, RefObject, useEffect, useRef, useState, type CSSProperties, type FC } from "react";

export type InViewAnimation = "slideUp" | "fadeIn" | "fadeUp" | "fadeDown" | "fadeLeft" | "fadeRight" | "zoom" | "none";

export type IdleAnimation = "pulse" | "float" | "breathe" | "wiggle" | "shine" | "bounce" | "none";

export type ScrollTransform = {
  /** The property to transform (x, y, rotate, scale, opacity, etc.) */
  property: string;

  /** Input range for the transform (typically 0 to 1 for scroll progress) */
  inputRange: number[];

  /** Output range for the transform */
  outputRange: number[] | string[];

  /** Optional easing function name */
  easing?: string;
};

export interface InViewProps {
  /** The content to be animated */
  children: ReactNode;

  /** Animation type to use for entrance */
  animation?: InViewAnimation;

  /** Transition properties for the entrance animation */
  transitionProperties?: {
    [key: string]: any;
  };

  /** Animation to loop after entrance animation completes */
  idleAnimation?: IdleAnimation;

  /** Delay before entrance animation starts (in seconds) */
  delay?: number;

  /** Duration of the entrance animation (in seconds) */
  duration?: number;

  /** Duration of the idle animation (in seconds) */
  idleDuration?: number;

  /** Delay before starting idle animation after entrance completes (in seconds) */
  idleDelay?: number;

  /** Only play the entrance animation once */
  once?: boolean;

  /** Amount of element that needs to be visible to trigger animation (0-1) */
  amount?: number;

  /** CSS className for the component */
  className?: string;

  /** Inline styles for the component */
  style?: CSSProperties;

  /** Additional margin around the root (must be specified in px or %) */
  margin?: string;

  /** Event triggered when animation completes */
  onAnimationComplete?: () => void;

  /** Disable idle animation (useful for conditional control) */
  disableIdle?: boolean;

  /** Enable scroll-based animations */
  withScroll?: boolean;

  /** Reference to element to track scroll against (defaults to window) */
  scrollTarget?: RefObject<HTMLElement>;

  /** Offset for scroll tracking: [startOffset, endOffset] */
  scrollOffset?: [string, string];

  /** List of properties to transform based on scroll position */
  scrollTransforms?: ScrollTransform[];

  /** Access the raw scroll progress value */
  onScrollProgress?: (progress: MotionValue<number>) => void;
}

/**
 * InView - A component that triggers animations when scrolled into view
 * and supports looping idle animations after entrance and scroll-based transformations
 */
export const InView: FC<InViewProps> = ({
  children,
  animation = "fadeIn",
  transitionProperties = {},
  idleAnimation = "none",
  delay = 0,
  duration = 0.5,
  idleDuration = 2,
  idleDelay = 0.2,
  once = false,
  amount = 0.3,
  className = "",
  style = {},
  margin = "0px",
  onAnimationComplete,
  disableIdle = false,
  withScroll = false,
  scrollTarget,
  scrollOffset = ["start", "end"],
  scrollTransforms = [],
  onScrollProgress,
}) => {
  // create ref for the element
  const ref = useRef(null);

  // track if entrance animation has completed
  const [entranceComplete, setEntranceComplete] = useState(false);

  // check if element is in view
  const isInView = useInView(ref, {
    once,
    amount,
    margin: margin as any,
  });

  // handle scroll-based animations
  const { scrollYProgress } = useScroll({
    target: withScroll ? ref : scrollTarget,
    offset: scrollOffset as any,
  });

  // call onScrollProgress callback if provided
  useEffect(() => {
    if (onScrollProgress && withScroll) {
      onScrollProgress(scrollYProgress);
    }
  }, [onScrollProgress, scrollYProgress, withScroll]);

  // generate transformed values based on scroll
  // useTransform must be called in the same order on every render, so we use a stable for-loop.
  const scrollValues: Record<string, MotionValue<any>> = (() => {
    if (!withScroll || scrollTransforms.length === 0) return {};
    const result: Record<string, MotionValue<any>> = {};
    for (let i = 0; i < scrollTransforms.length; i++) {
      const transform = scrollTransforms[i];
      // eslint-disable-next-line react-hooks/rules-of-hooks
      result[transform.property] = useTransform(
        scrollYProgress,
        transform.inputRange,
        transform.outputRange as any,
        typeof transform.easing === "function" ? { ease: transform.easing } : undefined
      );
    }
    return result;
  })();

  // reset entrance completion when out of view (if not once)
  useEffect(() => {
    if (!isInView && !once) {
      setEntranceComplete(false);
    }
  }, [isInView, once]);

  // handle entrance animation complete
  const handleEntranceComplete = () => {
    setEntranceComplete(true);
    if (onAnimationComplete) {
      onAnimationComplete();
    }
  };

  // define entrance animation variants
  const getEntranceVariants = (): Variants => {
    // base transition configuration
    const transition = {
      duration,
      delay,
      ease: "easeOut",
      ...transitionProperties,
    };

    // define hidden and visible states for each animation type
    switch (animation) {
      case "slideUp":
        return {
          hidden: { opacity: 1, translateY: 50, transition },
          visible: {
            opacity: 1,
            translateY: 0,
            transition,
          },
        };
      case "fadeIn":
        return {
          hidden: { opacity: 0, transition },
          visible: {
            opacity: 1,
            transition,
          },
        };

      case "fadeUp":
        return {
          hidden: { opacity: 0, translateY: 50, transition },
          visible: {
            opacity: 1,
            translateY: 0,
            transition,
          },
        };

      case "fadeDown":
        return {
          hidden: { opacity: 0, translateY: -50, transition },
          visible: {
            opacity: 1,
            translateY: 0,
            transition,
          },
        };

      case "fadeLeft":
        return {
          hidden: { opacity: 0, x: -50, transition },
          visible: {
            opacity: 1,
            x: 0,
            transition,
          },
        };

      case "fadeRight":
        return {
          hidden: { opacity: 0, x: 50, transition },
          visible: {
            opacity: 1,
            x: 0,
            transition,
          },
        };

      case "zoom":
        return {
          hidden: { opacity: 0, scale: 0.9, transition },
          visible: {
            opacity: 1,
            scale: 1,
            transition,
          },
        };

      case "none":
      default:
        return {
          hidden: { transition },
          visible: { transition },
        };
    }
  };

  // define idle animation variants
  const getIdleVariants = (): Variants => {
    // base transition configuration for idle animations
    const transition = {
      duration: idleDuration,
      repeat: Infinity,
      repeatType: "reverse" as const,
      ease: "easeInOut",
      delay: idleDelay,
      // add any additional transition properties here
      ...transitionProperties,
    };

    // define animations for each idle type
    switch (idleAnimation) {
      case "pulse":
        return {
          idle: {
            scale: [1, 1.03, 1],
            opacity: 1, // Ensure visibility during idle animation
            transition: {
              ...transition,
              times: [0, 0.5, 1],
            },
          },
        };

      case "float":
        return {
          idle: {
            translateY: [0, -10, 0],
            opacity: 1, // Ensure visibility during idle animation
            transition: {
              ...transition,
              times: [0, 0.5, 1],
            },
          },
        };

      case "breathe":
        return {
          idle: {
            opacity: [1, 0.8, 1],
            filter: ["brightness(1)", "brightness(1.1)", "brightness(1)"],
            transition: {
              ...transition,
              times: [0, 0.5, 1],
            },
          },
        };

      case "wiggle":
        return {
          idle: {
            rotate: [0, 1, 0, -1, 0],
            opacity: 1, // Ensure visibility during idle animation
            transition: {
              ...transition,
              times: [0, 0.25, 0.5, 0.75, 1],
            },
          },
        };

      case "shine":
        return {
          idle: {
            boxShadow: ["0 0 0 rgba(255,255,255,0)", "0 0 10px rgba(255,255,255,0.5)", "0 0 0 rgba(255,255,255,0)"],
            opacity: 1, // Ensure visibility during idle animation
            transition: {
              ...transition,
              times: [0, 0.5, 1],
            },
          },
        };

      case "bounce":
        return {
          idle: {
            translateY: [0, -12, 0],
            opacity: 1, // Ensure visibility during idle animation
            transition: {
              ...transition,
              type: "spring",
              stiffness: 300,
              damping: 10,
              times: [0, 0.5, 1],
            },
          },
        };

      default:
        return {};
    }
  };

  const entranceVariants = getEntranceVariants();
  const idleVariants = getIdleVariants();

  // combine entrance and idle variants
  const variants = {
    ...entranceVariants,
    ...idleVariants,
  };

  // for visibility, ensure the visible state has opacity: 1
  if (variants.visible && typeof variants.visible === "object") {
    variants.visible.opacity = 1;
  }

  // determine current animation state
  const getCurrentAnimationState = () => {
    if (!isInView) {
      return "hidden";
    }

    if (entranceComplete && idleAnimation !== "none" && !disableIdle) {
      return "idle";
    }

    return "visible";
  };

  // combine scroll-driven styles with regular styles
  const combinedStyle = { ...style };

  // add scroll-driven values to style if withScroll is enabled
  if (withScroll && Object.keys(scrollValues).length > 0) {
    // convert MotionValues to actual style properties
    Object.entries(scrollValues).forEach(([key, value]) => {
      (combinedStyle as Record<string, any>)[key] = value;
    });
  }

  return (
    <motion.div
      ref={ref}
      className={className}
      style={combinedStyle}
      initial="hidden"
      animate={getCurrentAnimationState()}
      variants={variants}
      onAnimationComplete={() => {
        if (!entranceComplete && isInView) {
          handleEntranceComplete();
        }
      }}
    >
      {children}
    </motion.div>
  );
};
